import ReactGA from "react-ga4";

const GA_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;

export const initGA = () => {
  if (!GA_ID) return;
  ReactGA.initialize(GA_ID, {
    gaOptions: { send_page_view: false },
  });
};

export const trackPageView = (path, title) => {
  if (!GA_ID) return;
  ReactGA.send({ hitType: "pageview", page: path, title });
};

// ── Ecommerce item shape ──────────────────────────────────────────────────────
const toGAItem = (product, quantity = 1, index = 0) => ({
  item_id: product._id || product.id,
  item_name: product.name,
  item_category: product.category?.name || product.category || "Grocery",
  price: product.sellPrice || product.price || 0,
  quantity,
  index,
});

// ── Product events ─────────────────────────────────────────────────────────────
export const trackViewItem = (product) => {
  if (!GA_ID) return;
  ReactGA.event("view_item", {
    currency: "INR",
    value: product.sellPrice || 0,
    items: [toGAItem(product)],
  });
};

export const trackAddToCart = (product, quantity = 1) => {
  if (!GA_ID) return;
  ReactGA.event("add_to_cart", {
    currency: "INR",
    value: (product.sellPrice || 0) * quantity,
    items: [toGAItem(product, quantity)],
  });
};

export const trackRemoveFromCart = (product, quantity = 1) => {
  if (!GA_ID) return;
  ReactGA.event("remove_from_cart", {
    currency: "INR",
    value: (product.sellPrice || 0) * quantity,
    items: [toGAItem(product, quantity)],
  });
};

// ── Cart / Checkout funnel ────────────────────────────────────────────────────
export const trackViewCart = (cartItems, cartTotal) => {
  if (!GA_ID) return;
  ReactGA.event("view_cart", {
    currency: "INR",
    value: cartTotal,
    items: cartItems.map((item, i) =>
      toGAItem(item.productId || item, item.quantity, i)
    ),
  });
};

export const trackBeginCheckout = (cartItems, cartTotal, couponCode) => {
  if (!GA_ID) return;
  ReactGA.event("begin_checkout", {
    currency: "INR",
    value: cartTotal,
    coupon: couponCode || undefined,
    items: cartItems.map((item, i) =>
      toGAItem(item.productId || item, item.quantity, i)
    ),
  });
};

export const trackAddShippingInfo = (cartItems, cartTotal, shippingTier) => {
  if (!GA_ID) return;
  ReactGA.event("add_shipping_info", {
    currency: "INR",
    value: cartTotal,
    shipping_tier: shippingTier,
    items: cartItems.map((item, i) =>
      toGAItem(item.productId || item, item.quantity, i)
    ),
  });
};

export const trackAddPaymentInfo = (cartItems, cartTotal, paymentType) => {
  if (!GA_ID) return;
  ReactGA.event("add_payment_info", {
    currency: "INR",
    value: cartTotal,
    payment_type: paymentType,
    items: cartItems.map((item, i) =>
      toGAItem(item.productId || item, item.quantity, i)
    ),
  });
};

export const trackPurchase = (orderId, cartItems, revenue, shipping, couponCode) => {
  if (!GA_ID) return;
  ReactGA.event("purchase", {
    transaction_id: orderId,
    currency: "INR",
    value: revenue,
    shipping,
    coupon: couponCode || undefined,
    items: cartItems.map((item, i) =>
      toGAItem(item.productId || item, item.quantity, i)
    ),
  });
};

// ── Search ────────────────────────────────────────────────────────────────────
export const trackSearch = (searchTerm, resultsCount) => {
  if (!GA_ID) return;
  ReactGA.event("search", { search_term: searchTerm, results_count: resultsCount });
};

// ── Auth ──────────────────────────────────────────────────────────────────────
export const trackLogin = (method = "phone_otp") => {
  if (!GA_ID) return;
  ReactGA.event("login", { method });
};

export const trackSignUp = (method = "phone_otp") => {
  if (!GA_ID) return;
  ReactGA.event("sign_up", { method });
};

// ── Custom ────────────────────────────────────────────────────────────────────
export const trackCouponApplied = (couponCode, discount) => {
  if (!GA_ID) return;
  ReactGA.event("coupon_applied", { coupon_code: couponCode, discount_value: discount });
};
